import { LiveData, Service } from '@toeverything/infra';
import { distinctUntilChanged, map, of, switchMap } from 'rxjs';

import { AuthService, type WorkspaceServerService } from '../../cloud';
import type { GlobalCache } from '../../storage/providers/global';
import type { WorkspaceService } from '../../workspace';

const DEFAULT_COLLAPSABLE_STATE: Record<string, boolean> = {
  workspace: false,
  recent: true,
  favorites: true,
  organize: false,
  collections: true,
  tags: true,
  favoritesOld: true,
  migrationFavorites: true,
  others: false,
};

export class NavigationPanelService extends Service {
  constructor(
    private readonly globalCache: GlobalCache,
    private readonly workspaceService: WorkspaceService,
    private readonly workspaceServerService: WorkspaceServerService
  ) {
    super();
  }

  private readonly collapsedCache = new Map<string, LiveData<boolean>>();
  private readonly sidebarAreaHeightCache = new Map<
    string,
    LiveData<number | null>
  >();

  private accountIdForServer(
    server: WorkspaceServerService['server'],
    liveAccountId?: string | null
  ) {
    return (
      liveAccountId ??
      server?.confirmedAccountId ??
      server?.cachedAccountId ??
      '__local__'
    );
  }

  private accountId() {
    const server = this.workspaceServerService.server;
    const authService = server?.scope.getOptional(AuthService);
    return this.accountIdForServer(
      server,
      authService?.session.account$.value?.id
    );
  }

  private accountId$() {
    return this.workspaceServerService.server$.pipe(
      switchMap(server => {
        const authService = server?.scope.getOptional(AuthService);
        return (authService?.session.account$.asObservable() ?? of(null)).pipe(
          map(account => this.accountIdForServer(server, account?.id))
        );
      }),
      distinctUntilChanged()
    );
  }

  private key(pathKey: string, accountId = this.accountId()) {
    return `navigation:account:${accountId}:workspace:${this.workspaceService.workspace.id}:${pathKey}`;
  }

  private legacyWorkspaceKey(pathKey: string) {
    return `navigation:${this.workspaceService.workspace.id}:${pathKey}`;
  }

  collapsed$(path: string[], defaultCollapsed = true) {
    const pathKey = path.join(':');
    const cached$ = this.collapsedCache.get(pathKey);
    if (!cached$) {
      const legacyKey = this.legacyWorkspaceKey(pathKey);
      const liveData$ = LiveData.from(
        this.accountId$().pipe(
          switchMap(accountId =>
            this.globalCache.watch<boolean>(this.key(pathKey, accountId))
          )
        ),
        this.globalCache.get<boolean>(this.key(pathKey)) ??
          this.globalCache.get<boolean>(legacyKey) ??
          DEFAULT_COLLAPSABLE_STATE[pathKey] ??
          defaultCollapsed
      ).map(
        collapsed =>
          collapsed ??
          this.globalCache.get<boolean>(legacyKey) ??
          DEFAULT_COLLAPSABLE_STATE[pathKey] ??
          defaultCollapsed
      );
      this.collapsedCache.set(pathKey, liveData$);
      return liveData$;
    }
    return cached$;
  }

  setCollapsed(path: string[], collapsed: boolean) {
    const pathKey = path.join(':');
    this.globalCache.set(this.key(pathKey), collapsed);
  }

  sidebarAreaHeight$(path: string[]) {
    const pathKey = `sidebarAreaHeight:${path.join(':')}`;
    const cached$ = this.sidebarAreaHeightCache.get(pathKey);
    if (cached$) return cached$;

    const normalize = (height: number | null | undefined) =>
      typeof height === 'number' && Number.isFinite(height) && height >= 0
        ? height
        : null;
    const liveData$ = LiveData.from(
      this.accountId$().pipe(
        switchMap(accountId =>
          this.globalCache.watch<number>(this.key(pathKey, accountId))
        )
      ),
      normalize(this.globalCache.get<number>(this.key(pathKey)))
    ).map(normalize);
    this.sidebarAreaHeightCache.set(pathKey, liveData$);
    return liveData$;
  }

  setSidebarAreaHeight(path: string[], height: number) {
    if (!Number.isFinite(height) || height < 0) return;
    const pathKey = `sidebarAreaHeight:${path.join(':')}`;
    this.globalCache.set(this.key(pathKey), height);
  }
}
